/**
 * Offline Sync Service
 * Handles syncing pending offline data when connection is restored
 */

import {
  STORES,
  getPendingVisitNotes,
  removePendingVisitNote,
  updateItem,
  getPendingCount,
  type PendingVisitNote,
} from '@/lib/storage/offlineDB'
import { createVisitNote } from './visitNotes'
import type { UserRole, VisitNoteInput, VisitPhotoType } from '@/types'

const MAX_SYNC_RETRIES = 20

// Sync status type
export interface SyncStatus {
  isSyncing: boolean
  lastSyncAt: Date | null
  pendingCount: number
  failedCount: number
  errors: SyncError[]
}

export interface SyncError {
  id: string
  type: string
  message: string
  timestamp: Date
}

// Event types
type SyncEventType = 'sync-start' | 'sync-complete' | 'sync-error' | 'sync-progress' | 'status-change'

interface SyncEvent {
  type: SyncEventType
  data?: {
    progress?: number
    total?: number
    error?: SyncError
    status?: SyncStatus
  }
}

type SyncEventListener = (event: SyncEvent) => void

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (Object.prototype.toString.call(value) !== '[object Object]') return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function stripUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .filter(item => item !== undefined)
      .map(item => stripUndefinedDeep(item)) as T
  }

  if (isPlainObject(value)) {
    const cleaned: Record<string, unknown> = {}
    for (const [key, nestedValue] of Object.entries(value)) {
      if (nestedValue !== undefined) {
        cleaned[key] = stripUndefinedDeep(nestedValue)
      }
    }
    return cleaned as T
  }

  return value
}

function buildVisitNoteInput(note: PendingVisitNote): VisitNoteInput {
  const data = note.data

  return stripUndefinedDeep({
    visitId: data.visitId,
    clientId: data.clientId,
    summary: data.summary,
    clientCondition: data.clientCondition as VisitNoteInput['clientCondition'],
    clientMood: data.clientMood,
    activitiesCompleted: data.activitiesCompleted,
    medicationsAdministered: data.medicationsAdministered,
    vitalSigns: data.vitalSigns as VisitNoteInput['vitalSigns'],
    tasksCompleted: (data.tasksCompleted || []).map(task => ({
      taskId: task.taskId,
      description: task.description,
      completed: task.completed,
      notes: task.notes,
    })),
    photos: data.photos?.map(photo => ({
      id: photo.id,
      type: photo.type as VisitPhotoType,
      dataUrl: photo.dataUrl,
      caption: photo.caption,
      timestamp: photo.timestamp instanceof Date ? photo.timestamp : new Date(),
    })),
    signature: data.signature,
    aiGenerated: data.aiGenerated,
    aiTranscript: data.aiTranscript,
    documentationTime: data.documentationTime,
    travelTime: data.travelTime,
    timeIn: data.timeIn,
    timeOut: data.timeOut,
    hcpcsCode: data.hcpcsCode,
    locationCode: data.locationCode,
    billable: data.billable,
    clinicianCredentials: data.clinicianCredentials,
    clinicalScales: data.clinicalScales as VisitNoteInput['clinicalScales'],
    missedVisit: data.missedVisit,
    missedVisitReason: data.missedVisitReason,
    prn: data.prn,
    external: data.external,
    attachments: data.attachments,
  })
}

// Sync manager singleton
class OfflineSyncManager {
  private static instance: OfflineSyncManager
  private isSyncing = false
  private syncInterval: ReturnType<typeof setInterval> | null = null
  private listeners: Set<SyncEventListener> = new Set()
  private status: SyncStatus = {
    isSyncing: false,
    lastSyncAt: null,
    pendingCount: 0,
    failedCount: 0,
    errors: [],
  }

  // Store bound handlers to properly remove listeners
  private boundHandleOnline: () => void
  private boundHandleOffline: () => void

  private constructor() {
    // Bind handlers once so they can be properly removed
    this.boundHandleOnline = () => this.handleOnline()
    this.boundHandleOffline = () => this.handleOffline()

    // Initialize online/offline listeners
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.boundHandleOnline)
      window.addEventListener('offline', this.boundHandleOffline)

      // Start periodic sync check
      this.startPeriodicSync()
    }
  }

  static getInstance(): OfflineSyncManager {
    if (!OfflineSyncManager.instance) {
      OfflineSyncManager.instance = new OfflineSyncManager()
    }
    return OfflineSyncManager.instance
  }

  // Event handling
  addEventListener(listener: SyncEventListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private emit(event: SyncEvent): void {
    this.listeners.forEach(listener => listener(event))
  }

  private updateStatus(updates: Partial<SyncStatus>): void {
    this.status = { ...this.status, ...updates }
    this.emit({ type: 'status-change', data: { status: this.status } })
  }

  // Get current status
  getStatus(): SyncStatus {
    return { ...this.status }
  }

  // Check if online
  isOnline(): boolean {
    return typeof navigator !== 'undefined' ? navigator.onLine : true
  }

  // Handle coming online
  private async handleOnline(): Promise<void> {
    console.log('[OfflineSync] Connection restored, initiating sync...')
    await this.sync()
  }

  // Handle going offline
  private handleOffline(): void {
    console.log('[OfflineSync] Connection lost')
    if (this.isSyncing) {
      this.isSyncing = false
      this.updateStatus({ isSyncing: false })
    }
  }

  // Start periodic sync (every 30 seconds when online)
  private startPeriodicSync(): void {
    if (this.syncInterval) return

    this.syncInterval = setInterval(async () => {
      if (this.isOnline() && !this.isSyncing) {
        const count = await this.getPendingCount()
        if (count > 0) {
          await this.sync()
        }
      }
    }, 30000) // 30 seconds
  }

  // Get total pending count
  async getPendingCount(): Promise<number> {
    try {
      const visitNotes = await getPendingCount(STORES.PENDING_VISIT_NOTES)
      const incidents = await getPendingCount(STORES.PENDING_INCIDENTS)
      const total = visitNotes + incidents
      this.updateStatus({ pendingCount: total })
      return total
    } catch (error) {
      console.error('[OfflineSync] Error getting pending count:', error)
      return 0
    }
  }

  // Main sync function
  async sync(): Promise<SyncStatus> {
    if (this.isSyncing || !this.isOnline()) {
      return this.status
    }

    this.isSyncing = true
    this.updateStatus({ isSyncing: true, errors: [] })
    this.emit({ type: 'sync-start' })

    try {
      // Sync visit notes
      await this.syncVisitNotes()

      // Update status
      const pendingCount = await this.getPendingCount()
      this.updateStatus({
        isSyncing: false,
        lastSyncAt: new Date(),
        pendingCount,
      })

      this.emit({ type: 'sync-complete' })
    } catch (error) {
      console.error('[OfflineSync] Sync failed:', error)
      const syncError: SyncError = {
        id: `error_${Date.now()}`,
        type: 'sync_failed',
        message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date(),
      }
      this.updateStatus({
        isSyncing: false,
        errors: [...this.status.errors, syncError],
      })
      this.emit({ type: 'sync-error', data: { error: syncError } })
    } finally {
      this.isSyncing = false
    }

    return this.status
  }

  // Sync visit notes
  private async syncVisitNotes(): Promise<void> {
    const pendingNotes = await getPendingVisitNotes()
    const total = pendingNotes.length

    if (total === 0) return

    console.log(`[OfflineSync] Syncing ${total} pending visit notes...`)

    let completed = 0
    let failed = 0

    for (const note of pendingNotes) {
      try {
        if (note.retryCount >= MAX_SYNC_RETRIES) {
          console.warn(`[OfflineSync] Visit note ${note.id} exceeded retry limit`)
          failed++
          continue
        }

        // Attempt to sync
        await createVisitNote({
          data: buildVisitNoteInput(note),
          agencyId: note.data.agencyId,
          caregiverId: note.data.caregiverId,
          userId: note.data.userId,
          userEmail: note.data.userEmail,
          userRole: note.data.userRole as UserRole,
        })

        // Success - remove from pending
        await removePendingVisitNote(note.id)
        completed++

        // Emit progress
        this.emit({
          type: 'sync-progress',
          data: { progress: completed, total },
        })

        console.log(`[OfflineSync] Visit note ${note.id} synced successfully`)
      } catch (error) {
        console.error(`[OfflineSync] Failed to sync visit note ${note.id}:`, error)

        // Update retry count
        const updatedNote: PendingVisitNote = {
          ...note,
          retryCount: note.retryCount + 1,
          lastRetry: Date.now(),
          error: error instanceof Error ? error.message : 'Unknown error',
        }
        await updateItem(STORES.PENDING_VISIT_NOTES, updatedNote)
        failed++

        const syncError: SyncError = {
          id: `error_${note.id}_${Date.now()}`,
          type: 'visit_note_sync_failed',
          message: `Failed to sync shift note: ${error instanceof Error ? error.message : 'Unknown error'}`,
          timestamp: new Date(),
        }
        this.updateStatus({
          errors: [...this.status.errors, syncError],
          failedCount: this.status.failedCount + 1,
        })
      }
    }

    console.log(`[OfflineSync] Visit notes sync complete. Success: ${completed}, Failed: ${failed}`)
  }

  // Force sync
  async forceSync(): Promise<SyncStatus> {
    if (!this.isOnline()) {
      throw new Error('Cannot sync while offline')
    }
    return this.sync()
  }

  // Clear all pending data (use with caution)
  async clearPendingData(): Promise<void> {
    const { clearStore } = await import('@/lib/storage/offlineDB')
    await clearStore(STORES.PENDING_VISIT_NOTES)
    await clearStore(STORES.PENDING_INCIDENTS)
    await clearStore(STORES.SYNC_QUEUE)
    this.updateStatus({ pendingCount: 0, failedCount: 0, errors: [] })
  }

  // Cleanup
  destroy(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval)
      this.syncInterval = null
    }
    this.listeners.clear()
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.boundHandleOnline)
      window.removeEventListener('offline', this.boundHandleOffline)
    }
  }
}

// Export singleton instance
export const offlineSyncManager = OfflineSyncManager.getInstance()

// Convenience exports
export const syncNow = () => offlineSyncManager.forceSync()
export const getSyncStatus = () => offlineSyncManager.getStatus()
export const isOnline = () => offlineSyncManager.isOnline()
export const onSyncStatusChange = (listener: SyncEventListener) =>
  offlineSyncManager.addEventListener(listener)
